import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import styled from "styled-components";
import type { DividerPolicy } from "@CommonTypes/backend/PspRules";
import {
  canonicalTellOChipText,
  TELL_O_MAX_TEXT_LENGTH,
  describeTellOShelfSaved,
  describeTellOShelfUndone,
  type RuleProposal,
  type TellOChip,
  type TellOReplyAction,
  type TellOShelfProposal,
  type TellOTranslate,
} from "@CommonUtils/pog/tellO";
import { Color } from "src/elements";
import { useTranslation } from "src/i18n";
import type { TranslationKey } from "src/i18n/hooks/UseTranslation";
import { OMark } from "../components/OSummary";
import type { TellOInterpret, TellOSaveShelf } from "./tellOApi";
import {
  DEFAULT_POLICY,
  applyProposal,
  describeReply,
  isDefaultPolicy,
  isOtherStoreShelf,
  isUndoLast,
  readTellOText,
  tellOChips,
  tellOContext,
  withWindowPrevious,
  type TellOWindowData,
} from "./tellOModel";

export const TELL_O_THINKING_AFTER_MS = 300;

const DEFAULT_CAPTION_MS = 3000;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Field = styled.input`
  flex: 1;
  min-width: 0;
  font: inherit;
  font-size: 13px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 6px;
  background: ${Color.white};
  color: ${Color.textMain};

  &:focus {
    outline: none;
    border-color: ${Color.primary};
  }

  &:disabled {
    background: ${Color.greenSmokeDisabled};
  }
`;

const PrimaryButton = styled.button`
  font: inherit;
  font-size: 12px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${Color.primary};
  border-radius: 6px;
  background: ${Color.primary};
  color: ${Color.white};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${Color.primaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.button`
  font: inherit;
  font-size: 12px;
  padding: 3px 10px;
  border: 1px solid ${Color.primary};
  border-radius: 999px;
  background: ${Color.white};
  color: ${Color.primary};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${Color.greenSmoke};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const LinkButton = styled.button`
  font: inherit;
  font-size: 12px;
  padding: 0;
  border: 0;
  background: none;
  color: ${Color.primary};
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${Color.primaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 2px;
  }

  &:disabled {
    color: ${Color.textSecondary};
    cursor: default;
    text-decoration: none;
  }
`;

const CaptionText = styled.span`
  font-size: 12px;
  color: ${Color.textSecondary};
`;

const Reply = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid ${Color.greenSmoke};
`;

const ReplyBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin-top: 4px;
`;

const Muted = styled.span`
  font-size: 12px;
  color: ${Color.textSecondary};
`;

type Caption = "thinking" | "offline" | "failed" | "default" | null;

type CommonKey = TranslationKey<"common">;

const CAPTION_KEYS: Record<Exclude<Caption, null>, CommonKey> = {
  thinking: "planogramRules.tellO.thinking",
  offline: "configCopilot.offline",
  failed: "planogramRules.tellO.failed",
  default: "planogramRules.tellO.backToDefaultDone",
};

interface OtherStoreReply {
  kind: "other-store";
}

interface Pending {
  proposal: Exclude<RuleProposal, { kind: "undo" }> | OtherStoreReply;
  policy: DividerPolicy;
  // A follow-up selection must not ask the user a second question.
  fromQuestion?: boolean;
}

type ShelfStatus =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved"; proposal: TellOShelfProposal }
  | { status: "failed"; message: string };

const previousOf = (proposal: TellOShelfProposal): TellOShelfProposal =>
  proposal.feature === "dividers"
    ? { ...proposal, dividersSetByHand: proposal.previous ?? null }
    : { ...proposal, loaderDepth: proposal.previous ?? null };

const followsRule = (proposal: TellOShelfProposal) =>
  proposal.feature === "dividers"
    ? proposal.dividersSetByHand === null
    : proposal.loaderDepth === null;

const NOT_UNDERSTOOD: RuleProposal = { kind: "cannot", reason: { code: "not-understood" } };

export interface TellOProps {
  policy: DividerPolicy;
  setPolicy: (policy: DividerPolicy) => void;
  data: TellOWindowData;

  previous: DividerPolicy | null;
  setPrevious: (policy: DividerPolicy | null) => void;
  isDetailsOpen: boolean;
  setIsDetailsOpen: (isOpen: boolean) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isFreeTextDisabled?: boolean;

  basePogId?: number | null;
  interpret?: TellOInterpret;
  saveShelf?: TellOSaveShelf;
}

export const TellO = ({
  policy,
  setPolicy,
  data,
  previous,
  setPrevious,
  isDetailsOpen,
  setIsDetailsOpen,
  isLoading = false,
  isDisabled = false,
  isFreeTextDisabled = false,
  basePogId,
  interpret,
  saveShelf,
}: TellOProps) => {
  const { t } = useTranslation("common");
  const translateCopy = useMemo<TellOTranslate>(
    () => (key, params) => t(`planogramRules.tellO.copy.${key}`, params),
    [t],
  );
  const [text, setText] = useState("");
  const [caption, setCaption] = useState<Caption>(null);
  const [shelfCaption, setShelfCaption] = useState<TellOShelfProposal | null>(null);
  const [prefillChip, setPrefillChip] = useState<TellOChip | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [shelf, setShelf] = useState<ShelfStatus>({ status: "idle" });
  const [isAsking, setIsAsking] = useState(false);
  const [selection, setSelection] = useState<[number, number] | null>(null);
  const fieldRef = useRef<HTMLInputElement>(null);
  const freeTextHelpId = useId();
  const askId = useRef(0);

  const shelfId = useRef(0);
  const captionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(captionTimer.current), []);

  useEffect(() => {
    if (!selection || !fieldRef.current) return;
    fieldRef.current.focus();
    fieldRef.current.setSelectionRange(selection[0], selection[1]);
    setSelection(null);
  }, [selection]);

  const context = useMemo(() => tellOContext(policy, data), [policy, data]);
  const chips = useMemo(
    () => (isLoading ? [] : tellOChips(context, previous !== null, translateCopy)),
    [context, previous, isLoading, translateCopy],
  );
  const reply = useMemo(() => {
    if (!pending) return null;
    if (pending.proposal.kind === "other-store") {
      const storeCode = context.baseStoreCode;
      return {
        proposal: pending.proposal,
        sentence: storeCode
          ? t("planogramRules.tellO.otherStore", { storeCode })
          : t("planogramRules.tellO.otherStoreUnknown"),
        rationale: null,
        preview: null,
        actions: [{ label: translateCopy("neverMind") }],
      };
    }
    // A rule Apply replaces both product lists, so its policy must still match.
    if (pending.proposal.kind !== "shelf" && pending.policy !== policy) return null;
    const proposal =
      pending.fromQuestion && pending.proposal.kind === "question"
        ? NOT_UNDERSTOOD
        : pending.proposal;
    const described = describeReply(context, proposal, translateCopy);
    return {
      proposal,
      ...described,
      sentence:
        proposal.kind === "cannot" && proposal.reason.code === "not-understood"
          ? t("planogramRules.tellO.notUnderstood")
          : described.sentence,
    };
  }, [pending, policy, context, t, translateCopy]);

  const isBusy = isDisabled || shelf.status === "saving";

  const showCaption = (next: Caption) => {
    clearTimeout(captionTimer.current);
    setCaption(next);
    if (next === "default") {
      captionTimer.current = setTimeout(() => setCaption(null), DEFAULT_CAPTION_MS);
    }
  };

  const dropAsk = () => {
    askId.current += 1;
    setIsAsking(false);
  };

  const backToDefault = () => {
    if (isDisabled) return;
    dropAsk();
    setPending(null);
    if (!isDefaultPolicy(policy)) {
      setPrevious(policy);
      setPolicy(DEFAULT_POLICY);
    }
    showCaption("default");
  };

  const undoLast = () => {
    if (isDisabled) return;
    dropAsk();
    setPending(null);
    if (!previous) {
      backToDefault();
      return;
    }
    setPolicy(previous);
    setPrevious(null);
    showCaption(isDefaultPolicy(previous) ? "default" : null);
  };

  const propose = (proposal: RuleProposal, fromQuestion = false) => {
    dropAsk();
    shelfId.current += 1;
    setShelf({ status: "idle" });
    setShelfCaption(null);
    if (proposal.kind === "undo") {
      backToDefault();
      return;
    }
    showCaption(null);

    const shown: Pending["proposal"] = isOtherStoreShelf(context, proposal)
      ? { kind: "other-store" }
      : proposal.kind === "shelf"
        ? withWindowPrevious(context, proposal)
        : proposal;
    setPending({ proposal: shown, policy, ...(fromQuestion ? { fromQuestion } : {}) });
  };

  const ask = async () => {
    const asked = text.trim();
    if (!asked || isLoading || isDisabled || isFreeTextDisabled) return;
    if (isUndoLast(asked)) {
      undoLast();
      return;
    }
    const read = readTellOText(context, canonicalTellOChipText(prefillChip, asked));
    // A PSA base has no store code; the endpoint can resolve the flow’s store.

    const needsStore =
      read?.kind === "cannot" &&
      read.reason.code === "every-store-override" &&
      read.reason.storeCode === null &&
      !context.baseStoreCode;
    if (read && !needsStore) {
      propose(read);
      return;
    }
    setPending(null);
    if (!interpret || basePogId === null || basePogId === undefined) {
      showCaption("offline");
      return;
    }
    const id = ++askId.current;
    const thinking = setTimeout(() => {
      if (askId.current === id) showCaption("thinking");
    }, TELL_O_THINKING_AFTER_MS);
    setIsAsking(true);
    const result = await interpret({ text: asked, rule: policy, unit: context.unit, basePogId });
    clearTimeout(thinking);
    if (askId.current !== id) return;
    setIsAsking(false);
    if (result.status === "offline") showCaption("offline");
    else if (result.status === "failed") showCaption("failed");
    else if (result.status === "refused") propose(NOT_UNDERSTOOD);
    else propose(result.response.proposal);
  };

  const tapChip = (chip: TellOChip) => {
    if (isDisabled) return;
    if (chip.kind === "prefill") {
      dropAsk();
      showCaption(null);
      setPrefillChip(chip);
      setText(chip.text);
      setSelection(chip.selection);
    } else if (chip.proposal.kind === "undo") {
      undoLast();
    } else {
      propose(chip.proposal);
    }
  };

  const apply = async (proposal: RuleProposal) => {
    if (isBusy) return;
    if (proposal.kind === "rule") {
      dropAsk();
      setPrevious(policy);
      setPolicy(applyProposal(policy, proposal, data));
      setPending(null);
      setText("");
      return;
    }
    if (proposal.kind === "shelf" && saveShelf) {
      const id = ++shelfId.current;
      setShelf({ status: "saving" });
      const notSaved = await saveShelf(proposal);
      if (shelfId.current !== id) return;
      setShelf(
        notSaved === null ? { status: "saved", proposal } : { status: "failed", message: notSaved },
      );
    }
  };

  const undoShelf = async (proposal: TellOShelfProposal) => {
    if (!saveShelf || isBusy) return;
    const before = previousOf(proposal);
    const id = ++shelfId.current;
    setShelf({ status: "saving" });
    const notSaved = await saveShelf(before);
    if (shelfId.current !== id) return;
    if (notSaved !== null) {
      setShelf({ status: "failed", message: notSaved });
      return;
    }
    setPending(null);
    setShelf({ status: "idle" });
    setShelfCaption(before);
  };

  const close = () => {
    shelfId.current += 1;
    setPending(null);
    setShelf({ status: "idle" });
  };

  const editText = (next: string) => {
    setText(next);
    setShelfCaption(null);
    if (isAsking) {
      dropAsk();
      showCaption(null);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void ask();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      editText("");
    }
  };

  const hasApply = reply?.proposal.kind === "rule" || reply?.proposal.kind === "shelf";

  const renderAction = (action: TellOReplyAction) => {
    const { proposal } = action;
    if (action.primary && proposal) {
      return (
        <PrimaryButton
          key={action.label}
          type="button"
          onClick={() => void apply(proposal)}
          disabled={isBusy || (proposal.kind === "shelf" && !saveShelf)}
        >
          {action.label}
        </PrimaryButton>
      );
    }
    if (!proposal) {
      return hasApply ? (
        <LinkButton key={action.label} type="button" onClick={close}>
          {action.label}
        </LinkButton>
      ) : (
        <Chip key={action.label} type="button" onClick={close}>
          {action.label}
        </Chip>
      );
    }
    return (
      <Chip key={action.label} type="button" onClick={() => propose(proposal, true)}>
        {action.label}
      </Chip>
    );
  };

  const savedShelf =
    reply?.proposal.kind === "shelf" && shelf.status === "saved" ? reply.proposal : null;

  return (
    <Card data-tell-o>
      <FieldRow>
        <Field
          ref={fieldRef}
          type="text"
          aria-label={t("planogramRules.tellO.link")}
          aria-describedby={isFreeTextDisabled ? freeTextHelpId : undefined}
          placeholder={t("planogramRules.tellO.placeholder")}
          maxLength={TELL_O_MAX_TEXT_LENGTH}
          value={text}
          disabled={isLoading || isDisabled || isFreeTextDisabled}
          onChange={(event) => editText(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <PrimaryButton
          type="button"
          onClick={() => void ask()}
          disabled={isLoading || isDisabled || isFreeTextDisabled || isAsking || text.trim() === ""}
        >
          {t("planogramRules.tellO.send")}
        </PrimaryButton>
        {previous && (
          <LinkButton type="button" data-undo onClick={undoLast} disabled={isDisabled}>
            {t("planogramRules.tellO.undo")}
          </LinkButton>
        )}
        {!isLoading && !isDefaultPolicy(policy) && (
          <LinkButton type="button" onClick={backToDefault} disabled={isDisabled}>
            {t("planogramRules.tellO.backToDefault")}
          </LinkButton>
        )}
      </FieldRow>
      {isFreeTextDisabled && !isLoading && (
        <Muted id={freeTextHelpId} role="note" data-free-text-disabled>
          {t("planogramRules.tellO.freeTextDisabledInBaseMode")}
        </Muted>
      )}
      {(caption || shelfCaption) && (
        <CaptionText role="status" data-caption={caption ?? "shelf"}>
          {caption
            ? t(CAPTION_KEYS[caption])
            : shelfCaption &&
              (followsRule(shelfCaption)
                ? describeTellOShelfUndone(shelfCaption, translateCopy)
                : describeTellOShelfSaved(shelfCaption, translateCopy))}
        </CaptionText>
      )}
      {!isLoading && (
        <ChipRow data-chips>
          {chips.map((chip) => (
            <Chip
              key={chip.label}
              type="button"
              disabled={isDisabled}
              onClick={() => tapChip(chip)}
            >
              {chip.label}
            </Chip>
          ))}
        </ChipRow>
      )}
      {reply && (
        <Reply data-reply={reply.proposal.kind} aria-live="polite">
          <OMark aria-hidden="true" />
          <ReplyBody>
            <span data-reply-sentence>{reply.sentence}</span>
            {reply.rationale && <Muted data-rationale>{reply.rationale}</Muted>}
            {reply.preview && <span data-reply-preview>{reply.preview}</span>}
            {savedShelf && shelf.status === "saved" ? (
              <Actions>
                <Muted role="status">
                  {describeTellOShelfSaved(shelf.proposal, translateCopy)}
                </Muted>
                <LinkButton
                  type="button"
                  onClick={() => void undoShelf(savedShelf)}
                  disabled={isBusy}
                >
                  {t("planogramRules.tellO.undo")}
                </LinkButton>
              </Actions>
            ) : hasApply ? (
              <Actions>
                {shelf.status === "failed" && <Muted role="alert">{shelf.message}</Muted>}
                {reply.actions.map(renderAction)}
              </Actions>
            ) : (
              <ChipRow>{reply.actions.map(renderAction)}</ChipRow>
            )}
          </ReplyBody>
        </Reply>
      )}
      <div>
        <LinkButton
          type="button"
          aria-expanded={isDetailsOpen}
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          disabled={isDisabled}
        >
          {t(
            isDetailsOpen ? "planogramRules.tellO.hideDetails" : "planogramRules.tellO.showDetails",
          )}
        </LinkButton>
      </div>
    </Card>
  );
};
